/**
 * This is an extension of the default kendo ListView.  It gives reorderable option.
 *
 * The move hint will be the row, and any elements marked with the class "removeOnMove" removed.
 *
 * Created by rjulius on 2/3/15.
 */
import BaseWidget from "common/widgets/base/BaseWidget";
import DFValidator from "common/validator/DFValidator";
import reorder from "common/helper/reorder";
import DFPager from "app/widgets/DFPager/DFPager";
import locale from "common/managers/locale";
import paging from "common/managers/paging";
import SavedSelection from "./SavedSelection";
import domUtils from "common/helper/domUtils";
import scroll from "common/helper/scroll";

const DFListView = BaseWidget(kendo.ui.ListView).extend({

    // SavedSelection for tracking selected items from readonly state toggle
    _readonlySelection: undefined,

    // the sortable widget
    _sortable: undefined,

    options: {

        name: "DFListView",

        /** Standard Kendo ListView option. Adjust default back to null from <div>. This prevents massive breakage in existing code. */
        contentElement: null,

        /** Should the ListView make attempts to minimize the changes to the DOM instead of doing full refreshes */
        minimalRefresh: false,

        /** When selectable == multiple, should the user be able to drag to select? */
        dragToSelect: true,

        /**
         * When set to a data item's field, that field is used to order the grid, users can also
         * drag and drop rows to re-order them.
         */
        reorderableRows: false,

        /**
         * When true, reorderableRows will automatically sort the datasource. It will also automatically
         * reorder to indexes.
         */
        reorderableSort: true,

        /** Passed through to Sortable widget. When true, container will also be set. */
        autoScroll: false,

        // whether to skip the scroll reset in autoScroll if there is nothing selected
        skipScrollReset: false,

        // scroll to the selected item when a selection is made
        scrollToSelection: false,

        /** Passed through to Sortable widget **/
        handler: null,
        /** Passed through to Sortable widget.  If not set, the filter will be all 'li' elements in the list. **/
        filter: null,
        /** Passed through to Sortable widget. If not set, 'textarea' and 'input' elements will be ignored. **/
        ignore: null,
        /** Passed through to Sortable widget. If not passed, item being sorted will be cloned. **/
        hint: null,
        /**
         * Add in a pager to control the list.  Uses paging configuration as designed for Main.
         */
        pager: false,
        /**
         * Change default of displaying info. We typically do NOT display this, but you can set this option to true to display it.
         */
        pagerInfo: false,
        /**
         * Typically, we do not allow the user to change page sizes. Set this to give a dropdown of page sizes, pulled from user settings.
         */
        includePageSizes: false,
        /**
         * If set to true, kendo.bind will be called on all widgets in the template.  They will be bound to the
         * observable for that list item.
         */
        bindWidgets: false,
        /**
         * Causes the listview to restore the selection on refresh of the grid
         */
        keepSelectionOnRefresh: false,

        // whether right clicking an item should select it.  Set to the jQuery selector for an item, i.e. li[data-uid]
        selectOnContext: "",

        /** Whether count of total results is imprecise. */
        impreciseCount: false,

        // when changing to readonly mode, we change the DS to display only selected items. Hold on to the full DS here.
        _fullDatasource: undefined,

        // the selectable option, when in edit / main mode. Changes to
        _editableSelectable: "single",

        // field on the model that holds the value
        dataValueField: null,

        // whether the value should be primitive
        valuePrimitive: false,

        doubleClickSelector: false,

        //Should enter be considered equivalent to a double click
        doubleClickOnEnterKey: false,
    },

    events: [
        "sortChange",
        "startReorder"
    ],

    init: function() {
        kendo.ui.ListView.fn.init.apply(this, arguments);
        this.wrapper.toggleClass("navigatable", this.options.navigatable);

        var me = this;

        if (!this.element.attr("id")) {
            console.error("DFListView Widget widgets require an id");
        }

        // hold on to the selectable option so we can restore it as we flip readonly state
        this._editableSelectable = this.options.selectable;

        if (this.options.selectable == "multiple" && !this.options.dragToSelect) {
            me.selectable.userEvents.unbind("start");
            me.selectable.userEvents.unbind("move");
            me.selectable.userEvents.unbind("end");
            me.selectable.userEvents.unbind("select");
        }

        this._initReorderable();
        this._initSortable();
        this._prepareEditing();
        this._prepareDoubleClick();
        this._prepareSelectContext();
        this._prepareDisableHook();

        this._pageable();
        this.refresh();
    },

    refresh: function(e) {
        if (!this.trigger("considerRefresh", {
                originalEvent: e
            })) {
            if (this.options.minimalRefresh && e && e.action == "add" && e.items.length == 1) {
                this._addRefresh(e.items[0], e.index);
            } else {
                var selection;
                if (this.options.keepSelectionOnRefresh) {
                    selection = new SavedSelection(this);
                    selection.save();
                }

                var auto = this._preRefreshForAutoScroll();
                kendo.ui.ListView.fn.refresh.apply(this, arguments);
                if (selection && !this.trigger("considerRestoreSelection", {
                        originalEvent: e
                    })) {
                    selection.restore();
                }
                this._updatePager();
                this._makeWidgetsActive();
                this.autoScroll(auto);
            }
        }
    },

    _addRefresh: function(item, index) {
        if (this.trigger("dataBinding", {
                action: "add",
                items: [item],
                index: index
            })) {
            return;
        }
        var html = this.template(item);
        var el = $(html).attr(kendo.attr("uid"), item.uid)
            .attr("role", "option")
            .attr("aria-selected", "false")
            .addClass("k-listview-item");
        var children = this.content.children();
        if (index >= children.length) {
            this.content.append(el);
        } else {
            var after = $(children[index]);
            after.before(el);
        }
        this.trigger("dataBound");
    },

    setDataSource: function(dataSource) {
        kendo.ui.ListView.fn.setDataSource.apply(this, arguments);
        this._pageable();
    },

    _preRefreshForAutoScroll: function() {
        return this.content.scrollTop();
    },

    /**
     * Scroll to the first selected item, or to the top if none.
     */
    autoScroll: function(initial) {
        // find first selected
        var row = undefined;
        if (this.options.selectable) {
            var selected = this.select();
            if (selected.length) {
                row = selected.first();
            }
        }

        if (initial !== undefined) {
            this.content.scrollTop(initial);
        }

        if (!row) {
            if (this.options.skipScrollReset !== true) {
                domUtils.scrollToTop(this.content);
            }
        } else {
            scroll.scrollToElement(row.get(0), this.content.get(0));
        }
    },

    /**
     * Apply default focus.
     * @param selector {string} jquery selector to find the element to focus
     */
    focus: function(selector) {
        if (!selector) {
            this.element.focus();
        } else {
            this.element.find(selector).focus();
        }
    },

    _makeWidgetsActive: function() {
        if (this.options.bindWidgets) {
            var me = this;
            this.content.find("li").each(function(i, el) {
                el = $(el);
                kendo.bind(el, me.dataSource.getByUid(el.attr("data-uid")));
                me.trigger("widgetsBoundForRow", {
                    row: el
                });
                me.one("dataBinding", function() {
                    kendo.destroy(el);
                })
            })
        }
    },

    /**
     * Init reorder on the widget datasource
     * @private
     */
    _initReorderable: function() {
        if (!this.options.reorderableRows || !this.options.reorderableSort) {
            return;
        }
        this.dataSource.sort({
            field: this.options.reorderableRows,
            dir: "asc"
        });
    },

    /**
     * Make this widget sortable if the option is enabled
     * @private
     */
    _initSortable: function() {
        if (!this.options.reorderableRows) {
            return;
        }
        const me = this;
        this._destroySortable();
        this._sortable = new kendo.ui.Sortable(this.content, {
            filter: this.options.filter ? this.options.filter : "li",
            cursor: "move",
            axis: "y",
            ignore: this.options.ignore ? this.options.ignore : "input, textarea",
            handler: this.options.handler || ".sortableDragButton",
            autoScroll: this.options.autoScroll,
            container: this.options.autoScroll ? this.content : null,
            hint: this.options.hint ? this.options.hint : function(row) {
                var moveRow = row.clone();
                moveRow.find(".removeOnMove").remove();
                return moveRow;
            },
            placeholder: function(element) {
                return element.clone().empty().addClass("placeholderBox padding-30");
            },
            start: function(e) {
                if (me.trigger("startReorder", {
                        item: me.dataItem(e.item)
                    })) {
                    e.preventDefault();
                }
            },
            change: function(e) {
                var ds = me.dataSource,
                    fromIndex = e.oldIndex,
                    toIndex = e.newIndex;


                if (fromIndex !== toIndex) {
                    if (me.options.reorderableSort) {
                        reorder(ds, fromIndex, toIndex, me.options.reorderableRows, me.options.reorderableRowsIndex);
                        ds.sort({
                            field: "ordinal",
                            dir: "asc"
                        });
                    }
                    me.trigger("sortChange", {
                        item: me.dataItem(e.item),
                        oldIndex: e.oldIndex,
                        newIndex: e.newIndex
                    });
                }
            }
        });
    },

    /**
     * Remove the sortable remnants
     * @private
     */
    _destroySortable: function() {
        if (this._sortable) {
            this._sortable.destroy();
            delete this._sortable;
        }
    },

    _prepareEditing: function() {
        var me = this;

        //Swap out the type of validator used during edit
        me.bind("edit", function(e) {
            me._destroySortable();
            var validator = e.item.data("kendoValidator");
            var editable = e.item.data("kendoEditable");
            if (validator && editable) {
                validator.destroy();
                editable.validatable = new DFValidator(e.item, {});
            }
        });
        this.bind(["save", "cancel"], function() {
            me._initSortable();
        });
    },

    _pageable: function() {
        if (this.options.pager) {
            if (this.customPager) {
                this.customPager.destroy();
            }
            // define the options for the pager control
            var settings = $.extend({}, {
                dataSource: this.dataSource,
                numeric: false,
                input: true,
                info: this.options.pagerInfo,
                messages: {
                    display: locale.get('pager.total') + '{2}',
                    itemsPerPage: locale.get('pager.itemsPerPage'),
                    page: locale.get('pager.page'),
                    empty: locale.get('pager.total') + '0'
                }
            });
            var Pager = DFPager;
            if (this.options.includePageSizes) {
                // If we are including a page size selector, we need to always display the pager.
                // To do this, just use the standard Pager, since the benefit to DFPager is auto-hiding.
                Pager = kendo.ui.Pager;
                // include the page size options
                settings = $.extend(settings, {
                    pageSizes: paging.pageSizes()
                });

            }
            if (!this.customPagerElement) {
                this.customPagerElement = $("<div>")
                    .attr("id", this.element.attr("id") + "_pager")
                    .addClass("listViewPager")
                    .insertAfter(this.element);
                this.element.addClass("clear");
            }

            // set up imprecise messages
            if (this.options.impreciseCount) {
                settings.messages.of = "";
            }

            this.customPager = new Pager(this.customPagerElement, settings);

            // add imprecise style
            if (this.options.impreciseCount) {
                this.customPager.element.addClass("imprecise-count-pager");
            }
        }
    },

    _cleanupPageable: function() {
        if (this.customPager && this.customPager.element) {
            this.customPager.destroy();
            delete this.customPager;
        }
        if (this.customPagerElement) {
            this.customPagerElement.remove();
            delete this.customPagerElement;
        }
    },

    _updatePager: function() {

        // update imprecise pager display
        if (this.options.impreciseCount && this.customPager) {
            var pagerOptions = this.customPager.options;
            pagerOptions.messages.display = (this.dataSource.page() == this.dataSource.totalPages()) ? locale.get("pager.display.precise") : locale.get("pager.display.imprecise");
            this.customPager.setOptions(pagerOptions);
        }
    },

    /**
     * Get all selected objects.
     * @returns {Array}
     */
    getSelectedDataItems: function() {
        var rows = this.select(),
            items = [],
            me = this;
        $.each(rows, function(i, val) {
            items.push(me.dataItem(val));
        });
        return items;
    },

    destroy: function() {
        this._destroySortable();
        this.element.off();
        kendo.ui.ListView.fn.destroy.apply(this, arguments);
        this._cleanupPageable();
    },

    /**
     * Setup doubleClick event.
     * @private
     */
    _prepareDoubleClick: function() {
        if (this.options.doubleClickSelector) {
            const doubleClick = (row) => {
                if(this.enabled) {
                    const item = this.dataItem(row);
                    const selected = $(row).is(".k-state-selected");
                    this.trigger("doubleClick", {
                        row: row,
                        item: item,
                        isSelected: selected,
                        index: this.content.find(this.options.doubleClickSelector).index(row)
                    });
                }
            };

            this.element.on("dblclick", this.options.doubleClickSelector, (e) => {
                if (!$(e.target).is("input, a")) {
                    doubleClick(e.currentTarget);
                }
            });

            if(this.options.doubleClickOnEnterKey) {
                this.element.on("keydown", (e) => {
                    if(e.keyCode === kendo.keys.ENTER) {
                        doubleClick(this.current());
                        e.preventDefault();
                        e.stopPropagation();
                    }
                });
            }
        }
    },

    /**
     * Handle the context menu.
     * @private
     */
    _prepareSelectContext: function() {
        var me = this;
        if (me.options.selectOnContext) {
            me.element.on("contextmenu", me.options.selectOnContext, function(e) {
                var found = false;
                var current = me.select();
                var target = e.currentTarget;
                for (var i = 0; i < current.length && !found; i++) {
                    if (current[i] == target) {
                        found = true;
                    }
                }
                if (!found) {
                    me.clearSelection();
                    me.select(target);
                }
            });
        }
    },

    enable: function(value) {
        this.enabled = value ? true : false;
        this.content.toggleClass("prevent-scroll", !this.enabled);
    },

    enabled: true,

    _prepareDisableHook: function() {
        var me = this;
        var stop = function(e) {
            if (!me.enabled) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        this.element[0].addEventListener("mousedown", stop, true);
        this.element[0].addEventListener("mouseup", stop, true);
        this.element[0].addEventListener("click", stop, true);
        this.element[0].addEventListener("pointerdown", stop, true);
        this.element[0].addEventListener("MSPointerDown", stop, true);
    },

    readonly: function(to) {
        this.options.readonly = to;
        if (to) {
            // set the datasource to include only the selected options
            this._fullDatasource = this.dataSource;
            if (this.options.selectable) {
                var me = this,
                    _selectedRows = this.select(),
                    _selectedItems = [];
                $.each(_selectedRows, function(i, item) {
                    _selectedItems.push(me.dataItem(item));
                });
                // save selection in case readonly is toggled
                this._readonlySelection = new SavedSelection(this);
                this._readonlySelection.save();
            } else {
                _selectedItems = this.dataSource.data();
            }
            this.setDataSource(new kendo.data.DataSource({
                data: _selectedItems
            }));
            // make the list unselectable
            this._editableSelectable = this.options.selectable;
            this.options.selectable = false;
        } else {
            // set the datasource back to the default / full datasource, with all items, not just those that were selected
            if (this._fullDatasource) {
                this.setDataSource(this._fullDatasource);
                delete this._fullDatasource;
            }
            // set selectable back to default - single or multiple
            this.options.selectable = this._editableSelectable;
            // restore selection
            if (this.options.selectable && this._readonlySelection) {
                this._readonlySelection.restore();
                delete this._readonlySelection;
            }
        }
    },

    /**
     * Getter/setter for row value.
     *
     * When a value is set, it will only select a row until the listview is refreshed.
     * The value binding handles keep the value sticky if necessary.
     * @param value
     */
    value: function(value) {
        var valueField = this.options.dataValueField;
        if (!valueField) {
            valueField = "id";
        }
        const valueItem = (item) => {
            if(this.options.valuePrimitive) {
                return item[valueField];
            }
            return item;
        };
        if(!this.selectable.options.multiple) {
            if(arguments.length) {
                var matches = this.dataSource.view().filter(function(item) {
                    return valueItem(item) == value;
                });
                if(matches.length == 1) {
                    this.select(this.content.find("[data-uid='" + matches[0].uid + "']"));
                } else if(this.select().length > 0) {
                    this.clearSelection();
                }
            } else {
                var item = this.dataItem(this.select());
                if(item) {
                    return valueItem(item);
                } else {
                    return null;
                }
            }
        } else {
            if(arguments.length) {
                const found = this.dataSource.view().filter((item) => {
                    return value.indexOf(valueItem(item)) >= 0;
                });
                if(found.length > 0) {
                    this.select(this.content.find(found.map((item) => {
                        return "[data-uid='" + item.uid + "']";
                    }).join()));
                } else {
                    this.clearSelection();
                }
            } else {
                return this.getSelectedDataItems().map((item) => {
                    return valueItem(item);
                });
            }
        }
    },

    /**
     * Override
     * kendo no longer triggers change on select() as of 2022.1.412 and we rely on it.
     */
    select() {
        if(arguments.length > 0) {
            const args = arguments;
            const res = this._checkForSelectChange(() => {
                return kendo.ui.ListView.fn.select.apply(this, args);
            });
            if(this.options.scrollToSelection) {
                this.autoScroll();
            }
            return res;
        } else {
            return kendo.ui.ListView.fn.select.call(this);
        }
    },

    clearSelection() {
        kendo.ui.ListView.fn.clearSelection.call(this);
        this.trigger("change");
    },

    _checkForSelectChange(action) {
        const before = this.select();
        const res = action();
        const after = this.select();
        const change = before.length !== after.length || before.length !== before.filter(after).length;
        if(change) {
            this.trigger("change");
        }
        return res;
    }
});

kendo.ui.plugin(DFListView);
export default DFListView;
